import { useState, useEffect, useCallback } from 'react';
import { safeRequestFullscreen } from '../lib/utils';
import { soundManager } from '../lib/sound';

export function useVoiceChat() {
  const [isMicMuted, setIsMicMuted] = useState(true);
  const [isVoiceActive, setIsVoiceActive] = useState(false);

  const toggleMute = async (networkRef: any) => {
    safeRequestFullscreen();
    if (!isVoiceActive) {
      if (networkRef.current) {
        try {
          await networkRef.current.initializeAudio();
          setIsVoiceActive(true);
          setIsMicMuted(false);
          networkRef.current.toggleMute(false);
        } catch (err) {
          console.error("[VoiceChat] Failed to manually initialize microphone:", err);
        } finally {
          let attempts = 0;
          const resumeFullscreen = () => {
            safeRequestFullscreen();
            attempts++;
            const doc = document as any;
            const isFs = !!(
              doc.fullscreenElement ||
              doc.webkitFullscreenElement ||
              doc.mozFullScreenElement ||
              doc.msFullscreenElement
            );
            if (isFs || attempts >= 3) {
              document.removeEventListener('touchstart', resumeFullscreen);
              document.removeEventListener('touchend', resumeFullscreen);
              document.removeEventListener('click', resumeFullscreen);
              document.removeEventListener('pointerdown', resumeFullscreen);
            }
          };

          // Try immediately with user activation if active
          safeRequestFullscreen();

          // Attach listeners for next user gesture on mobile
          document.addEventListener('touchstart', resumeFullscreen, { passive: true });
          document.addEventListener('touchend', resumeFullscreen, { passive: true });
          document.addEventListener('click', resumeFullscreen, { passive: true });
          document.addEventListener('pointerdown', resumeFullscreen, { passive: true });
        }
      }
    } else {
      setIsMicMuted((prev) => {
        const newState = !prev;
        networkRef.current?.toggleMute(newState);
        return newState;
      });
      safeRequestFullscreen();
    }
  };

  const handlePeerAudioStream = useCallback((peerId: string, stream: MediaStream) => {
    // Only mount and play remote audio if the stream actually has audio tracks
    const audioTracks = stream ? stream.getAudioTracks() : [];
    if (audioTracks.length === 0) return;

    let audioElement = document.getElementById(`audio-${peerId}`) as HTMLAudioElement | null;
    if (!audioElement) {
      audioElement = document.createElement("audio");
      audioElement.id = `audio-${peerId}`;
      audioElement.autoplay = true;
      audioElement.muted = false;
      audioElement.setAttribute("playsinline", "true");
      audioElement.setAttribute("webkit-playsinline", "true");
      (audioElement as any).playsInline = true;
      audioElement.style.position = "fixed";
      audioElement.style.top = "0px";
      audioElement.style.left = "0px";
      audioElement.style.width = "1px";
      audioElement.style.height = "1px";
      audioElement.style.opacity = "0.01";
      audioElement.style.pointerEvents = "none";
      audioElement.style.zIndex = "-9999";
      document.body.appendChild(audioElement);
    }

    const isMini = soundManager.isMusicMode();
    audioElement.volume = isMini ? 0.35 : 0.70;

    if (audioElement.srcObject !== stream) {
      audioElement.srcObject = stream;
    }

    // Play remote stream immediately as incoming audio should be audible to all players in the room
    const playPromise = audioElement.play();
    if (playPromise !== undefined) {
      playPromise.catch((err) => {
        // Autoplay may be deferred until user gesture; resumeAllRemoteAudios will unlock on click/touch
        console.debug(`[VoiceChat] Autoplay deferred for peer ${peerId}:`, err);
      });
    }
  }, []);

  const resumeAllRemoteAudios = useCallback(() => {
    soundManager.resume();

    const isMini = soundManager.isMusicMode();
    const audios = document.querySelectorAll(`audio[id^="audio-"]`);
    audios.forEach((audio) => {
      const ae = audio as HTMLAudioElement;
      ae.volume = isMini ? 0.35 : 0.70;
      if (ae.srcObject && ae.paused) {
        ae.play().catch(() => {});
      }
    });
  }, []);

  useEffect(() => {
    const handleMusicToggle = () => {
      const isMini = soundManager.isMusicMode();
      const audios = document.querySelectorAll(`audio[id^="audio-"]`);
      audios.forEach((audio) => {
        (audio as HTMLAudioElement).volume = isMini ? 0.40 : 0.75;
      });
    };
    window.addEventListener("musicModeToggle", handleMusicToggle);
    document.addEventListener("click", resumeAllRemoteAudios, { once: true, passive: true });
    document.addEventListener("touchstart", resumeAllRemoteAudios, { once: true, passive: true });
    return () => {
      window.removeEventListener("musicModeToggle", handleMusicToggle);
      document.removeEventListener("click", resumeAllRemoteAudios);
      document.removeEventListener("touchstart", resumeAllRemoteAudios);
    };
  }, [resumeAllRemoteAudios]);

  return {
    isMicMuted,
    setIsMicMuted,
    isVoiceActive,
    setIsVoiceActive,
    toggleMute,
    handlePeerAudioStream
  };
}


